import type { GuideSection } from './guides';

export type GuideSupplement = { keywords: string[]; sections: GuideSection[] };

export const guideSupplements: Record<string, GuideSupplement> = {
  "enhance-image-quality-without-overprocessing": {
    "keywords": [
      "enhance image quality",
      "AI photo enhancer",
      "improve photo clarity",
      "natural image enhancement",
      "avoid over sharpening",
      "photo quality improvement"
    ],
    "sections": [
      {
        "heading": "How to recognize overprocessing before you export",
        "paragraphs": [
          "Overprocessing usually shows up first in areas the camera captured imperfectly: skin becomes waxy, eyelashes merge into dark spikes, hair turns into repeated strands, and fine texture develops a crunchy outline. Another warning sign is local contrast that looks impressive at 25% zoom but produces halos around buildings, shoulders or tree branches at full size.",
          "Use a simple A/B check instead of judging the enhanced image alone. Toggle between the source and result at the same zoom, then ask whether the change improves readability without changing identity, material or geometry. If the enhancement adds detail you cannot explain from the source, reduce the strength or switch to a more specific repair tool."
        ],
        "bullets": [
          "Check skin, hair, fabric and foliage at 100% zoom.",
          "Look for light or dark halos on high-contrast edges.",
          "Verify logos, signs and small text character by character.",
          "Compare color before and after; clarity should not silently change product color."
        ]
      },
      {
        "heading": "A practical order for social, client and print delivery",
        "paragraphs": [
          "For social media, prioritize clean edges, moderate contrast and an output size close to the platform requirement; huge files rarely create a visible benefit after the platform recompresses them. For client delivery, keep a full-resolution master and create separate web copies so later resizing does not start from an already compressed image.",
          "For print, decide the required pixel dimensions before increasing resolution. Enhancement should come before the final resize when the source has noise or compression artifacts, because enlarging defects first makes them harder to control. Finish with a destination-specific export rather than one universal file for every use."
        ],
        "note": "A useful enhancement workflow is reversible: preserve the source, make one controlled change at a time and keep a high-quality master before creating delivery copies."
      }
    ]
  },
  "2x-vs-4x-image-upscaling": {
    "keywords": [
      "2x vs 4x upscaling",
      "8x image upscaler",
      "AI image upscale size",
      "increase photo resolution",
      "upscale for print",
      "image enlargement"
    ],
    "sections": [
      {
        "heading": "Choose the multiplier from the final pixel target",
        "paragraphs": [
          "Start with the destination, not the biggest available setting. If a marketplace needs a 2000-pixel-wide image and the source is 1200 pixels wide, 2× already clears the target. A 4× result would create extra processing time, a larger file and more reconstructed pixels without improving how the listing is displayed.",
          "The same rule applies to print. Determine the intended physical width and an appropriate pixels-per-inch target, then calculate the required width in pixels. Choose the smallest upscale multiplier that reaches that requirement with enough room for crop and layout."
        ],
        "bullets": [
          "1200 px source → 2400 px at 2×.",
          "1200 px source → 4800 px at 4×.",
          "1200 px source → 9600 px at 8×.",
          "Crop after calculating the final target so you do not accidentally fall below the required dimensions."
        ]
      },
      {
        "heading": "Why repeated upscaling is usually a poor substitute for one planned pass",
        "paragraphs": [
          "Running 2×, downloading, then running another 2× does not recreate the same information as a clean 4× workflow from the original. Each pass may interpret texture, sharpen edges and compress the export, so errors from the first pass can become source material for the second.",
          "When possible, return to the untouched original and choose the final multiplier once. If the source needs denoise, deblur or restoration, perform that correction first and then upscale the cleaned master. This gives the enlargement model fewer artifacts to amplify."
        ]
      }
    ]
  },
  "restore-old-family-photos": {
    "keywords": [
      "restore old photos",
      "old photo restoration AI",
      "repair damaged family photos",
      "restore faded photos",
      "remove photo scratches",
      "family photo archive"
    ],
    "sections": [
      {
        "heading": "Preserve historical evidence while improving readability",
        "paragraphs": [
          "A family photograph can contain details that matter beyond appearance: handwriting, uniforms, jewelry, house numbers, dates and facial features may all carry historical meaning. Restoration should make those elements easier to see without silently replacing them with plausible but invented alternatives.",
          "Keep a scan of the untouched print and treat strong generative repair as a derivative version. For important genealogy or archival work, label restored copies and retain the original scan so future viewers can distinguish captured evidence from reconstructed detail."
        ],
        "bullets": [
          "Never overwrite the archival scan.",
          "Check faces against other known photographs when identity matters.",
          "Keep handwritten notes and borders in a separate archival capture even if you crop them from display copies.",
          "Document strong colorization or facial reconstruction."
        ]
      },
      {
        "heading": "Restoration workflow for faded color prints",
        "paragraphs": [
          "Color fading often affects channels unevenly, creating a red, yellow, cyan or low-contrast cast. Correct global tonal balance before aggressive local detail enhancement; otherwise the model may sharpen discoloration and paper texture along with the subject.",
          "After color correction, inspect neutral objects that should be gray or white, then compare skin and clothing with any reliable reference. Historical film stocks and aged paper have characteristic color, so a perfectly neutral modern look is not always the most faithful restoration."
        ]
      }
    ]
  },
  "product-photo-quality-guide": {
    "keywords": [
      "product photo quality",
      "ecommerce product images",
      "Shopify image optimization",
      "marketplace product photography",
      "product photo enhancer",
      "online store images"
    ],
    "sections": [
      {
        "heading": "Build a repeatable catalog standard before editing",
        "paragraphs": [
          "Consistency is a quality signal in e-commerce. Define one aspect ratio, background treatment, subject scale, margin and lighting direction for the primary image, then apply the same standard across the catalog. This reduces visual jumps between listings and makes later batch preparation easier.",
          "Keep factual listing images separate from campaign images. The primary product image should represent the item accurately; lifestyle relighting, dramatic shadows and creative composites belong in secondary marketing assets where they cannot be mistaken for a feature of the product."
        ],
        "bullets": [
          "Use the same crop and product scale for comparable items.",
          "Check white balance against the real product.",
          "Preserve labels, stitching, ports, buttons and package text.",
          "Export a high-quality master before marketplace-specific compression."
        ]
      },
      {
        "heading": "Quality checks for zoom, mobile and marketplace compression",
        "paragraphs": [
          "A product photo can look excellent in a desktop editor and still fail after a marketplace resizes it. Preview the image at mobile thumbnail size to check silhouette and contrast, then at zoom size to inspect texture, label accuracy and edge artifacts.",
          "Avoid sharpening solely for the large preview; aggressive halos become obvious when the platform applies its own resize. A moderate master with clean edges usually survives multiple delivery sizes better than a file tuned to one exact screen."
        ]
      }
    ]
  },
  "fix-blurry-noisy-low-resolution-photos": {
    "keywords": [
      "fix blurry photo",
      "remove image noise",
      "low resolution photo fix",
      "AI deblur",
      "photo denoise",
      "increase image quality"
    ],
    "sections": [
      {
        "heading": "Use visual clues to diagnose the defect",
        "paragraphs": [
          "Motion blur usually stretches edges in a direction, camera shake can create doubled contours, focus blur softens edges without a clear direction, and noise appears as random grain or colored speckles. Low resolution is different again: edges become blocky or stair-stepped because too few pixels describe the scene.",
          "Zooming in helps, but do not diagnose from extreme magnification alone. Look at the image at 100% and at its intended viewing size. A defect that disappears at normal size may not need aggressive repair."
        ],
        "bullets": [
          "Directional streaks → deblur first.",
          "Random grain or chroma speckles → denoise first.",
          "Blocky pixels and too-small dimensions → upscale after cleanup.",
          "Clean but slightly soft edges → light sharpening may be enough."
        ]
      },
      {
        "heading": "Why the processing order changes the result",
        "paragraphs": [
          "Noise and compression artifacts should normally be reduced before upscaling because enlargement makes them larger and easier for an AI model to mistake for texture. Strong blur should be addressed before sharpening, because sharpening cannot restore a smeared edge; it only increases contrast around what remains.",
          "After the main defect is corrected, use enhancement or sharpening as a finishing step and compare against the original. This keeps each operation responsible for one visible problem and reduces the chance of an artificial result."
        ]
      }
    ]
  },
  "remove-image-background-clean-edges": {
    "keywords": [
      "remove image background",
      "background remover",
      "clean cutout edges",
      "transparent background",
      "remove background hair",
      "product background removal"
    ],
    "sections": [
      {
        "heading": "Prevent color fringing around the subject",
        "paragraphs": [
          "A cutout can be geometrically accurate and still look wrong because pixels along the edge contain color reflected from the old background. This is common with hair photographed against green, blue or bright studio paper and with glossy products that reflect their surroundings.",
          "Test the result on both a light and dark background. If a colored rim appears, refine or decontaminate the edge rather than shrinking the entire mask, which can make hair, fabric and thin product details look unnaturally clipped."
        ],
        "bullets": [
          "Inspect flyaway hair and fur on contrasting backgrounds.",
          "Check white products for gray or colored outlines.",
          "Check transparent plastics and glass separately from opaque edges.",
          "Avoid a hard one-pixel outline around soft-focus subjects."
        ]
      },
      {
        "heading": "Prepare cutouts for stores, design systems and social posts",
        "paragraphs": [
          "For reusable design assets, save a full-resolution transparent PNG or supported WebP master, then create smaller exports for the destination. For store listings that require white backgrounds, keep the transparent master anyway; it makes future seasonal or campaign layouts much easier.",
          "Maintain consistent canvas size and subject scale across a product collection. Background removal solves isolation, but professional presentation still depends on alignment, margins and realistic contact with the new background."
        ]
      }
    ]
  },
  "upscale-ai-art-for-print": {
    "keywords": [
      "upscale AI art",
      "AI art for print",
      "increase AI image resolution",
      "print AI artwork",
      "upscale Midjourney art",
      "poster resolution"
    ],
    "sections": [
      {
        "heading": "Protect linework, typography and repeated patterns",
        "paragraphs": [
          "AI artwork often contains detail that looks intentional from a distance but becomes ambiguous when enlarged. Fine linework can split, repeated ornament can mutate and pseudo-text can become more confidently wrong. Treat these regions as quality checkpoints instead of assuming a larger image is automatically print-ready.",
          "If the artwork contains real typography or logos, consider replacing those elements with vector or freshly typeset versions after upscaling. This preserves exact letterforms while allowing the surrounding illustration to benefit from AI enlargement."
        ]
      },
      {
        "heading": "Prepare a print master instead of only a large JPEG",
        "paragraphs": [
          "After upscaling, inspect the image at 100%, then create a master in a format and color workflow accepted by the print provider. Keep enough pixel dimensions for bleed and crop rather than matching only the visible final size.",
          "Do not repeatedly save a high-resolution JPEG during editing. Each lossy save can introduce ringing and block artifacts that become visible in smooth gradients or large-format prints. Keep an editing master and export the delivery file once at the end."
        ],
        "note": "Large-format prints are often viewed from farther away, so the printer’s real specification is more useful than chasing an arbitrary “8K” label."
      }
    ]
  },
  "jpg-png-webp-avif-image-format-guide": {
    "keywords": [
      "JPG vs PNG",
      "WebP vs AVIF",
      "best image format web",
      "image format comparison",
      "transparent image format",
      "image compression format"
    ],
    "sections": [
      {
        "heading": "Choose format after editing, not before",
        "paragraphs": [
          "During editing, preserve as much source information as practical. Converting a photo to a tiny JPEG before restoration or upscaling can throw away texture that later processing could have used. Do the quality-sensitive work first, then choose the delivery format based on transparency, browser support, file-size target and the destination’s upload rules.",
          "For repeated revisions, keep a high-quality master rather than downloading and re-uploading a compressed web copy. This prevents generation loss from becoming part of the source."
        ]
      },
      {
        "heading": "A simple decision table for common destinations",
        "paragraphs": [
          "Use JPEG when broad compatibility and efficient photographic compression matter and transparency is unnecessary. Use PNG for transparency, screenshots, interface graphics and sharp flat-color artwork when file size is acceptable. WebP is a practical web delivery format for photos or transparency, while AVIF can provide excellent compression when the browser and workflow support it.",
          "The “best” format is the one that preserves the required visual features at an acceptable size. Test representative images rather than applying one quality number to photographs, line art, screenshots and transparent assets alike."
        ]
      }
    ]
  },
  "how-to-fix-pixelated-images": {
    "keywords": [
      "fix pixelated image",
      "depixelate photo",
      "pixelated image enhancer",
      "upscale low resolution image",
      "make pixelated picture clear",
      "AI image upscaler"
    ],
    "sections": [
      {
        "heading": "Distinguish true pixelation from JPEG blocks",
        "paragraphs": [
          "True pixelation comes from too few source pixels or enlargement with poor interpolation. JPEG compression can look similar, but its blocks and ringing follow 8×8 compression regions and high-contrast edges. Treating both with maximum sharpening often makes the defect more obvious.",
          "If the image is both compressed and tiny, begin with artifact cleanup or gentle enhancement, then upscale from the cleanest available source. If an original camera file or larger export exists, using it will outperform any reconstruction of the smaller copy."
        ]
      },
      {
        "heading": "Graphics and logos need a different approach",
        "paragraphs": [
          "For logos, icons, diagrams and simple line art, vector reconstruction is often better than photographic AI upscaling because the intended shapes are geometric. If a vector original exists, export a new raster version at the required size rather than trying to repair a small screenshot.",
          "When no vector exists, upscale conservatively and inspect every curve, corner and letter. AI-generated edge detail may look smooth while subtly changing brand marks or typography."
        ]
      }
    ]
  },
  "image-resolution-dpi-print-size-guide": {
    "keywords": [
      "image resolution for print",
      "DPI vs PPI",
      "print size calculator",
      "pixels for 300 PPI",
      "photo print resolution",
      "upscale image for printing"
    ],
    "sections": [
      {
        "heading": "Pixels matter more than changing a DPI metadata field",
        "paragraphs": [
          "A file can say 72 DPI or 300 DPI without changing a single pixel. That metadata influences default physical size in some software, but it does not create new visual information. For print preparation, start with actual width and height in pixels and the intended print dimensions.",
          "For example, a 3000-pixel-wide image printed 10 inches wide provides 300 pixels per inch. The same image at 20 inches provides 150 pixels per inch. Whether that is enough depends on the printer, medium and viewing distance."
        ]
      },
      {
        "heading": "Allow for crop, bleed and layout changes",
        "paragraphs": [
          "Do not calculate print resolution from the uncropped source if the final layout will remove a large part of the image. Crop decisions reduce available pixels, and bleed can require additional image beyond the trimmed page edge.",
          "Ask the print provider for final dimensions, bleed and preferred color/file settings before the last upscale. This prevents producing a huge file that is still the wrong shape or missing required edge area."
        ]
      }
    ]
  },
  "remove-noise-from-low-light-photos": {
    "keywords": [
      "remove noise from photo",
      "low light photo denoise",
      "reduce ISO noise",
      "grainy photo fix",
      "AI denoise image",
      "night photo quality"
    ],
    "sections": [
      {
        "heading": "Treat luminance noise and color noise differently",
        "paragraphs": [
          "Luminance noise looks like brightness grain and can sometimes be left partly visible because a little fine grain preserves texture. Chroma noise appears as colored speckles or blotches and is usually more distracting, especially in shadows and skin. A strong one-size denoise can remove both but also erase pores, fabric weave and hair.",
          "Use the lowest reduction that solves the distracting artifact at the final viewing size. Night photos do not need to look like daylight captures; preserving some natural texture often produces a more believable image."
        ]
      },
      {
        "heading": "Denoise before heavy brightening or enlargement",
        "paragraphs": [
          "Lifting underexposed shadows reveals both subject detail and sensor noise. Similarly, upscaling makes noise patterns larger. If the source is visibly grainy, perform a controlled denoise before aggressive relighting or enlargement, then reassess the image.",
          "After denoise, avoid compensating with extreme sharpening. Add only enough local contrast to restore edge definition, and check smooth gradients such as skies and walls for banding or smeared patches."
        ]
      }
    ]
  },
  "fix-motion-blur-and-soft-focus": {
    "keywords": [
      "fix motion blur",
      "AI deblur photo",
      "camera shake fix",
      "soft focus photo repair",
      "sharpen blurry photo",
      "make blurry image clear"
    ],
    "sections": [
      {
        "heading": "Know when deblur cannot produce a trustworthy reconstruction",
        "paragraphs": [
          "If a face, license plate or text occupies only a handful of smeared pixels, there may be no unique correct detail to recover. AI can create a plausible-looking result, but plausibility is not the same as evidence. This matters for legal, identification, medical or documentary uses.",
          "For creative and personal images, judge whether the reconstruction looks natural and consistent with the rest of the frame. For evidentiary uses, keep the original and avoid representing generated detail as captured fact."
        ]
      },
      {
        "heading": "Finish with selective sharpening, not global intensity",
        "paragraphs": [
          "After motion blur is reduced, some edges may still feel soft. A restrained sharpening pass can improve local contrast, but global high-strength sharpening often creates halos around already-corrected edges and adds texture to smooth skin or sky.",
          "Evaluate the final image at its actual use size. A small web image can tolerate less recovered detail than a print crop, and chasing perfect sharpness at extreme zoom may make the normal view look harsh."
        ]
      }
    ]
  },
  "natural-ai-face-restoration-guide": {
    "keywords": [
      "AI face restoration",
      "restore blurry face",
      "face enhancer natural",
      "old photo face repair",
      "portrait enhancement",
      "preserve facial identity"
    ],
    "sections": [
      {
        "heading": "Identity-sensitive details deserve a separate review",
        "paragraphs": [
          "Facial restoration should preserve spacing and shape around the eyes, eyebrows, nose, mouth, jaw and hairline. Strong reconstruction can change age cues, skin texture, dental detail, makeup or expression even when the result appears attractive.",
          "Compare the restored face with the source and, when available, another verified photograph of the same person. If exact identity matters, prefer modest improvement over a dramatic “perfect” portrait."
        ],
        "bullets": [
          "Check eye shape and gaze direction.",
          "Check teeth and lip edges for invented structure.",
          "Check hairline, facial hair and wrinkles for age changes.",
          "Check earrings, glasses and other identity-adjacent details."
        ]
      },
      {
        "heading": "Portrait enhancement for modern headshots",
        "paragraphs": [
          "Modern headshots usually need less reconstruction than old or heavily compressed faces. Start with exposure, color, noise and mild clarity before using a specialist face-restoration model. Preserving realistic skin texture is usually more professional than removing every pore.",
          "If the image is for a profile, résumé or business page, verify that the result still represents the person as they actually appear. Enhancement should improve presentation, not become an undisclosed synthetic replacement."
        ]
      }
    ]
  },
  "optimize-images-for-web-without-quality-loss": {
    "keywords": [
      "optimize images for web",
      "reduce image file size",
      "WebP AVIF website images",
      "image compression SEO",
      "responsive images",
      "website performance images"
    ],
    "sections": [
      {
        "heading": "Resize before compression for the biggest practical saving",
        "paragraphs": [
          "A 5000-pixel camera photo displayed at 900 pixels wide wastes download bytes even if it is compressed. Create delivery dimensions close to the largest rendered size, then encode the image at a quality level that survives normal viewing. This usually saves more than repeatedly lowering JPEG quality on an oversized source.",
          "For responsive layouts, serve multiple widths so a phone does not download the desktop asset. Keep the original or a large master outside the delivery path for future redesigns."
        ]
      },
      {
        "heading": "Check performance and quality together",
        "paragraphs": [
          "File size is not the only metric. A very small image that contains banding, ringing or blurred product text can damage user trust, while an unnecessarily large image can slow the page and hurt interaction. Test representative pages on a real mobile connection and inspect the rendered image at common viewport sizes.",
          "Use modern formats where supported, but keep fallbacks or a processing pipeline that matches your audience. The best optimization is a balance of dimensions, compression, format and responsive delivery rather than a single “quality percentage.”"
        ]
      }
    ]
  },
  "increase-image-resolution-without-losing-quality": {
    "keywords": [
      "increase image resolution",
      "increase photo resolution online",
      "upscale image without losing quality",
      "AI resolution enhancer",
      "make image high resolution",
      "photo enlargement"
    ],
    "sections": [
      {
        "heading": "Resolution improvement starts with source selection",
        "paragraphs": [
          "If you have multiple copies of an image, compare their actual pixel dimensions and compression before processing. A messaging-app download, screenshot or social-media save may be much smaller than the camera original even when both look similar on a phone screen.",
          "Use the largest clean source available. AI upscaling is most valuable when the original cannot be recovered, not as a replacement for retrieving a better source file."
        ]
      },
      {
        "heading": "Measure success at the intended output size",
        "paragraphs": [
          "A high-resolution result is successful when it reaches the required dimensions and still looks natural in the final context. A huge file with artificial skin, broken text or invented patterns is not higher quality in a useful sense.",
          "After upscaling, inspect at 100% for defects and then zoom out to the actual print, web or presentation size. If 2× meets the requirement, a 4× or 8× version may only add reconstruction and storage cost."
        ]
      }
    ]
  },
  "how-to-make-a-picture-clearer": {
    "keywords": [
      "make picture clearer",
      "clear blurry picture",
      "photo clarity enhancer",
      "improve image sharpness",
      "AI photo clarity",
      "enhance blurry image"
    ],
    "sections": [
      {
        "heading": "Clarity is a combination of focus, contrast and clean texture",
        "paragraphs": [
          "A picture can feel unclear even when resolution is adequate. Haze, flat contrast, high noise and weak local edge definition all reduce perceived clarity. That is why simply increasing pixel dimensions does not always make an image look better.",
          "Identify which component is limiting the image. Denoise a grainy source, deblur directional smear, relight an underexposed subject, or use gentle enhancement when the file is merely soft and compressed."
        ]
      },
      {
        "heading": "Avoid the “clarity slider” trap",
        "paragraphs": [
          "Strong local-contrast effects can make a thumbnail look crisp while producing dark halos, emphasized pores and gritty skies. Use them as a finishing adjustment after the real defect is corrected rather than as a substitute for restoration.",
          "Compare the result on a normal-size screen and, for portraits, check skin separately from hair and clothing. The best setting may be different for different parts of the same image, so a conservative global result is safer when no local mask is available."
        ]
      }
    ]
  },
  "improve-phone-photo-quality": {
    "keywords": [
      "improve phone photo quality",
      "fix WhatsApp photo quality",
      "enhance mobile photo",
      "phone image compression",
      "make phone picture clearer",
      "AI phone photo enhancer"
    ],
    "sections": [
      {
        "heading": "Find the original before enhancing a shared copy",
        "paragraphs": [
          "Messaging apps and social platforms often resize or recompress images. If the sender still has the camera original, transferring it through cloud storage, a file-sharing service or an option that preserves original quality can provide a much better starting point than enhancing the chat preview.",
          "Check image dimensions before processing. A screenshot of a photo is usually a second-generation copy and may include display scaling or interface elements that reduce useful detail."
        ]
      },
      {
        "heading": "Common phone problems need different fixes",
        "paragraphs": [
          "Night-mode photos can contain denoising smears and sharpening halos; moving subjects can remain blurred even when the background is sharp; portrait mode can produce edge errors around hair; and digital zoom can create low-detail crops. Diagnose these separately instead of applying maximum enhancement to the whole frame.",
          "For future captures, clean the lens, stabilize the phone, avoid unnecessary digital zoom and expose for important highlights. Better capture quality reduces how much AI reconstruction is needed later."
        ]
      }
    ]
  },
  "denoise-vs-deblur-vs-sharpen": {
    "keywords": [
      "denoise vs deblur",
      "deblur vs sharpen",
      "image denoise tool",
      "AI deblur tool",
      "photo sharpening",
      "which photo enhancer tool"
    ],
    "sections": [
      {
        "heading": "A safe processing order for mixed defects",
        "paragraphs": [
          "When one photo has several defects, start with the most destructive one. Reduce severe noise before enlargement, correct directional blur before sharpening and postpone final crispness adjustments until the image has the intended dimensions.",
          "This order is not a rigid law, but it prevents common feedback loops where sharpening exaggerates noise, upscaling enlarges compression blocks or denoise erases edge detail that was already aggressively sharpened."
        ]
      },
      {
        "heading": "Use sharpening as a finish, not a rescue operation",
        "paragraphs": [
          "Sharpening increases contrast around existing edges. It works well on a clean image that is slightly soft after resize or denoise, but it cannot uniquely reconstruct a subject smeared across many pixels.",
          "If high sharpening is required just to make the image readable, return to the diagnosis. A deblur, denoise, higher-resolution source or specialist restoration step may solve the underlying problem more naturally."
        ]
      }
    ]
  },
  "fix-blurry-text-in-images-and-screenshots": {
    "keywords": [
      "fix blurry text image",
      "make screenshot text clear",
      "enhance document photo",
      "read blurry screenshot",
      "upscale text image",
      "sharpen scanned text"
    ],
    "sections": [
      {
        "heading": "Recover the source document whenever possible",
        "paragraphs": [
          "Text is unforgiving because one changed stroke can turn one character into another. If the image came from a PDF, website, presentation or chat attachment, retrieving the original document is safer than reconstructing a screenshot. For photographed paper, retaking the photo with better focus and light may also outperform AI repair.",
          "Use enhancement when the original is genuinely unavailable, but verify critical names, numbers, addresses and codes against another source before relying on them."
        ]
      },
      {
        "heading": "Process for legibility without inventing characters",
        "paragraphs": [
          "Start with perspective correction and even lighting for photographed documents, then reduce noise or blur before enlargement. Use restrained sharpening at the end to improve stroke contrast. Excessive reconstruction may produce crisp but incorrect letterforms.",
          "For accessibility or data entry, optical text recognition can be useful after cleanup, but the recognized text should still be checked against the visible source. AI-enhanced text should not be treated as proof of content that cannot be seen in the original."
        ]
      }
    ]
  },
  "scan-old-photos-for-ai-restoration": {
    "keywords": [
      "scan old photos",
      "best DPI for old photos",
      "photo scanning for restoration",
      "archive family photos",
      "scan prints high resolution",
      "old photo digitization"
    ],
    "sections": [
      {
        "heading": "Capture the whole object, not only the picture area",
        "paragraphs": [
          "For archival scans, include the full front and back of the print before making a cropped restoration copy. Borders, studio stamps, handwritten dates and notes can be important context even if they are removed from the display version.",
          "Save the raw or minimally processed scan first. Dust removal, color correction and AI restoration should create new derivative files rather than replacing the only digital capture."
        ]
      },
      {
        "heading": "Avoid scanner settings that permanently remove information",
        "paragraphs": [
          "Automatic sharpening, aggressive dust cleanup and heavy JPEG compression can bake artifacts into the scan before restoration begins. When the scanner allows it, use a high-quality lossless or lightly compressed format and disable enhancements you can apply later with more control.",
          "For reflective or curled prints that do not scan well, a carefully lit camera copy can be an alternative. Keep the camera parallel to the photograph, use even light from both sides and avoid reflections on glossy surfaces."
        ]
      }
    ]
  },
  "transparent-png-background-guide": {
    "keywords": [
      "transparent PNG",
      "remove background PNG",
      "alpha channel image",
      "transparent product image",
      "PNG background remover",
      "clean transparent edges"
    ],
    "sections": [
      {
        "heading": "Understand premultiplied edges and why halos appear",
        "paragraphs": [
          "Semi-transparent edge pixels contain both subject color and transparency information. Some editors or export pipelines mix these pixels against a matte color, which can create a light or dark fringe when the image is later placed on a different background.",
          "Test the exported asset in the actual destination rather than relying only on the checkerboard preview. If a halo appears, refine the alpha edge or export with settings that preserve clean unassociated transparency."
        ]
      },
      {
        "heading": "Keep a reusable master for future layouts",
        "paragraphs": [
          "A transparent master is more flexible than repeatedly removing a white background from new copies. Store the highest-quality cutout with intact alpha, then create web-sized PNG or WebP versions for each campaign, store or presentation.",
          "For product libraries, standardize the canvas, object scale and alignment as part of the asset system. Transparency solves the background, but consistent composition is what makes a collection look professional."
        ]
      }
    ]
  }
};
